import { Vector3 } from 'three';
import type { Input } from '../core/Input';
import type { MouseLook } from '../core/MouseLook';
import { accelerate, applyFriction } from './math';

const FLY_ACCEL = 12;
const FLY_SPEED = 1600; // u/s — fast enough to tour Source-scale geometry
const FLY_FRICTION = 6;
const FLY_STOP_SPEED = 100;

/**
 * Noclip fly controller (N toggles it in-game; primary in Phase 0, debug tool
 * after). Reuses the shared acceleration shape (project-and-cap, see
 * movement/math.ts) so the movement core is exercised even in noclip.
 */
export class FlyCamera {
  readonly position = new Vector3(0, 400, 1200);
  readonly velocity = new Vector3();

  private readonly wishDir = new Vector3();
  private readonly forward = new Vector3();
  private readonly right = new Vector3();

  constructor(
    private readonly input: Input,
    readonly look: MouseLook,
  ) {}

  update(dt: number): void {
    this.look.update();
    const { yaw, pitch } = this.look;

    const cosPitch = Math.cos(pitch);
    this.forward.set(-Math.sin(yaw) * cosPitch, Math.sin(pitch), -Math.cos(yaw) * cosPitch);
    this.right.set(Math.cos(yaw), 0, -Math.sin(yaw));

    const fwd = this.input.axis('KeyW', 'KeyS');
    const side = this.input.axis('KeyD', 'KeyA');
    const up = this.input.axis('Space', 'ShiftLeft');

    this.wishDir.set(0, 0, 0).addScaledVector(this.forward, fwd).addScaledVector(this.right, side);
    this.wishDir.y += up;

    applyFriction(this.velocity, FLY_FRICTION, FLY_STOP_SPEED, dt);

    const len = this.wishDir.length();
    if (len > 1e-6) {
      this.wishDir.divideScalar(len);
      accelerate(this.velocity, this.wishDir, FLY_SPEED, FLY_ACCEL, dt);
    }

    this.position.addScaledVector(this.velocity, dt);
  }
}
